import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'

import { FeatureType } from 'clickNgo/configs/feature'

import type {
  FlagRecord,
  FlagRecordAssignmentCount,
  FlagRecordDependency,
  FlagRecordEditableFields,
} from '../model/types'

import { syncFlagDependencyRecords } from './createFlagDependencyRecords'
import { fetchFlagAssignmentCount } from './fetchFlagAssignmentCount'
import { fetchFlagDependencyRecords } from './fetchFlagDependencyRecords'

export enum FlagManagementApiTags {
  FlagAssignments = 'FlagAssignments',
  FlagDependencies = 'FlagDependencies',
}

type FlagAssignmentCountQueryParams = {
  code: string
  type: string
}

type FlagDependencyRecordsQueryParams = {
  cacheKey?: string
  isForce?: boolean
}

type SyncFlagDependencyRecordsMutationParams = {
  dependencyRecords: FlagRecordDependency[]
  draft: FlagRecordEditableFields
  previousRecord: FlagRecord
}

const getQueryError = (error: unknown) => ({
  data: error instanceof Error ? error.message : String(error),
  status: 'CUSTOM_ERROR',
})

export const flagManagementApi = createApi({
  reducerPath: `${FeatureType.CngApi}/flagManagementApi`,
  baseQuery: fakeBaseQuery(),
  tagTypes: Object.values(FlagManagementApiTags),
  endpoints: (build) => ({
    getFlagAssignmentCount: build.query<
      FlagRecordAssignmentCount | null,
      FlagAssignmentCountQueryParams
    >({
      providesTags: (_result, _error, params) => [
        {
          type: FlagManagementApiTags.FlagAssignments,
          id: `${params.type}:${params.code}`,
        },
      ],
      queryFn: async (params) => {
        try {
          const data = await fetchFlagAssignmentCount(params)

          return { data }
        } catch (error) {
          return { error: getQueryError(error) }
        }
      },
    }),
    getFlagDependencyRecords: build.query<
      FlagRecordDependency[],
      FlagDependencyRecordsQueryParams | void
    >({
      providesTags: [{ type: FlagManagementApiTags.FlagDependencies, id: 'LIST' }],
      queryFn: async (params) => {
        try {
          const data = await fetchFlagDependencyRecords({
            cacheKey: params?.cacheKey,
            isForce: params?.isForce,
          })

          return { data }
        } catch (error) {
          return { error: getQueryError(error) }
        }
      },
    }),
    syncFlagDependencyRecords: build.mutation<void, SyncFlagDependencyRecordsMutationParams>({
      invalidatesTags: [{ type: FlagManagementApiTags.FlagDependencies, id: 'LIST' }],
      queryFn: async ({ dependencyRecords, draft, previousRecord }) => {
        try {
          await syncFlagDependencyRecords(previousRecord, draft, dependencyRecords)

          return { data: undefined }
        } catch (error) {
          return { error: getQueryError(error) }
        }
      },
    }),
  }),
})

export const {
  useGetFlagAssignmentCountQuery,
  useGetFlagDependencyRecordsQuery,
  useSyncFlagDependencyRecordsMutation,
} = flagManagementApi
